/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { useRouter, useTranslations } from "@i18n/next";
import { HallowAnalyticsEvent, useAnalytics } from "@packages/analytics";
import type { BibleBookId, BibleLanguage, BibleManifest } from "@packages/sdk";
import {
  ModalItem,
  ScrollArea,
  spacing,
  Text,
  useDynamicViewport,
  useModal,
  useScrollToOnLoad,
} from "@packages/ui";
import { useParams } from "next/navigation";
import type { ReactNode } from "react";
import { useCallback, useContext } from "react";

import { BibleLanguageContext } from "../BibleLanguageProvider";
import {
  BIBLE_LARGE_WIDTH,
  BIBLE_MEDIUM_WIDTH,
  isCustomDesktop,
  isLargeDevice,
  isMediumDevice,
} from "../bibleStyle.constants";
import type { RtlOrderFn } from "./BibleNavigation";

const chapterItem: Interpolation = {
  columnGap: spacing.s,
  justifyContent: "center",
  minWidth: "initial",
  width: spacing.xxxl,
};
const chapterSectionHeader: Interpolation = {
  display: "flex",
  gridColumn: "span 3",
  paddingLeft: spacing.m,
  placeItems: "center",
  [isCustomDesktop]: {
    display: "revert",
    gridColumn: "span 7",
    paddingLeft: "revert",
    placeItems: "revert",
  },
  [isLargeDevice]: {
    gridColumn: "span 7",
  },
  [isMediumDevice]: {
    gridColumn: "span 5",
  },
};
const chaptersStyle: Interpolation = {
  alignItems: "center",
  display: "grid",
  gridAutoFlow: "dense",
  gridAutoRows: "minmax(56px, min-content)",
  gridTemplateColumns: `repeat(3, ${spacing.xxxl})`,
  justifyContent: "space-evenly",
  [isCustomDesktop]: {
    gridTemplateColumns: `repeat(7, ${spacing.xxxl})`,
  },
  [isLargeDevice]: {
    gridTemplateColumns: `repeat(7, ${spacing.xxxl})`,
  },
  [isMediumDevice]: {
    gridTemplateColumns: `repeat(5, ${spacing.xxxl})`,
  },
};
const rtlItem: Interpolation = {
  flexDirection: "row-reverse",
};

type ChapterNavigatorProps = {
  manifest: BibleManifest;
  navBook: BibleBookId;
  rtlOrder: RtlOrderFn;
  language: BibleLanguage;
  className?: string;
};

export const ChapterNavigator = ({
  manifest,
  navBook,
  className = "",
  rtlOrder,
  language,
}: ChapterNavigatorProps) => {
  const modal = useModal();
  const isLargeDevice = useDynamicViewport({ minimumWidth: BIBLE_LARGE_WIDTH });
  const isMediumDevice = useDynamicViewport({
    minimumWidth: BIBLE_MEDIUM_WIDTH,
  });
  const { rtl } = useContext(BibleLanguageContext);
  const { translation, book, chapterVerse } = useParams();
  const analytics = useAnalytics();
  const t = useTranslations();
  const router = useRouter();
  const selectedChapterRef = useScrollToOnLoad<HTMLButtonElement>({
    block: "nearest",
  });

  const gotoChapter = useCallback(
    (cnum: number | string) => {
      analytics.track({
        event: HallowAnalyticsEvent.ChangedBibleChapter,
        properties: {
          bible_language: language,
          bible_translation: translation,
          bible_book_id: navBook,
          navigation_source: "direct_bible_nav",
          screen_name: "bible",
          starting_book: book,
          starting_chapter: chapterVerse?.[0],
          starting_translation: translation,
          new_chapter: cnum,
        },
      });
      analytics.track({
        event: HallowAnalyticsEvent.TappedBibleChapter,
        properties: {
          bible_language: language,
          bible_translation: translation,
          starting_chapter: chapterVerse?.[0],
          new_chapter: cnum,
        },
      });
      modal.remove();
      return router.push(`/bible/${translation}/${navBook}/${cnum}`);
    },
    [translation, navBook, language, chapterVerse],
  );

  const itemsPerRow = isLargeDevice ? 7 : isMediumDevice ? 5 : 3;
  const currentBook = manifest.books.find((b) => b.id === navBook);
  const currentBookChapters = currentBook?.chapter_count ?? 0;
  const chapters: ReactNode[] = [];
  if (currentBook?.has_intro) {
    const isSelected = chapterVerse?.[0] === "intro" && navBook === book;
    chapters.push(
      <ModalItem
        key={`${navBook}_intro_option`}
        selected={isSelected}
        onSelect={gotoChapter.bind(this, "intro")}
        css={[chapterItem, rtl ? rtlItem : null]}
        ref={isSelected ? selectedChapterRef : null}
        hideCheckOnSelected
        style={rtl ? { order: rtlOrder(0, itemsPerRow) } : undefined}
      >
        {t("bible_chapter_title_intro")}
      </ModalItem>,
    );
  }
  for (let i = 1; i <= currentBookChapters; i++) {
    const props = {
      style: rtl ? { order: rtlOrder(i, itemsPerRow) } : undefined,
      hideCheckOnSelected: true,
    };
    const isSelected = parseInt(chapterVerse?.[0]) === i && navBook === book;
    chapters.push(
      <ModalItem
        key={`${navBook}_${i}_option`}
        selected={isSelected}
        onSelect={gotoChapter.bind(this, i)}
        css={[chapterItem, rtl ? rtlItem : null]}
        ref={isSelected ? selectedChapterRef : null}
        {...props}
      >
        <>{i}</>
      </ModalItem>,
    );
  }
  if (chapters.length % itemsPerRow !== 0) {
    let i = chapters.length;
    do {
      chapters.push(
        <div
          key={`spacer-${i}`}
          style={rtl ? { order: rtlOrder(i, itemsPerRow) } : undefined}
          css={chapterItem}
        />,
      );
    } while (i++ % itemsPerRow !== 0); // intentional last round after i % # === 0
  }

  return (
    <ScrollArea viewportProps={{ className }}>
      <div css={chaptersStyle}>
        <Text as={"h2"} type={"title"} size={"l"} css={chapterSectionHeader}>
          {t("web_bible_navigation_chapter_section_title")}
        </Text>
        {chapters}
      </div>
    </ScrollArea>
  );
};
